import { VehicleInfoTonnagePriceCategoryType } from "../enums";

import { isNullOrUndefined } from "./any";

type Enum = Record<string, number> & Record<number, string>;

class EnumValues {
  static getNamesAndValues(e: Enum) {
    return this.getNames(e).map(x => ({ name: x, value: e[x] }));
  }

  private static getNames(e: Enum) {
    return Object.values(e).filter((v): v is string => typeof v === "string");
  }
}

type InferEnumValue<E> = E extends Record<string, infer V> ? (V extends number ? V : never) : never;

export function getEnumValue<E extends object>(value: unknown, enumeration: E): InferEnumValue<E> {
  if (isNullOrUndefined(value) || value === "") {
    return 0 as InferEnumValue<E>;
  }

  const enumValues = EnumValues.getNamesAndValues(enumeration as Enum);
  const stringValue = typeof value === "string" ? value.trim() : null;
  const normalizedString = stringValue ? stringValue.toLowerCase() : null;
  const intValue = typeof value === "number" ? value : parseInt(stringValue ?? "", 10);

  for (const current of enumValues) {
    if (normalizedString && current.name.toLowerCase() === normalizedString) {
      return current.value as InferEnumValue<E>;
    }
    if (current.value === intValue) {
      return current.value as InferEnumValue<E>;
    }
  }

  return (enumValues.length > 0 ? enumValues[0].value : 0) as InferEnumValue<E>;
}

export function getEnumName(value: unknown, enumeration: Record<number, string>): string | undefined {
  if (isNullOrUndefined(value) || value === "") {
    return undefined;
  }

  const enumValues = EnumValues.getNamesAndValues(enumeration as Enum);
  const stringValue = typeof value === "string" ? value.trim() : null;
  const normalizedString = stringValue ? stringValue.toLowerCase() : null;
  const intValue = typeof value === "number" ? value : parseInt(stringValue ?? "", 10);

  for (const current of enumValues) {
    if (normalizedString && current.name.toLowerCase() === normalizedString) {
      return current.name;
    }
    if (current.value === intValue) {
      return current.name;
    }
  }

  return undefined;
}

export function isEnumValue(value: unknown, enumeration: Record<number, string>): boolean {
  if (isNullOrUndefined(value) || value === "") {
    return false;
  }

  const enumValues = EnumValues.getNamesAndValues(enumeration as Enum);
  const stringValue = typeof value === "string" ? value.trim() : null;
  const normalizedString = stringValue ? stringValue.toLowerCase() : null;
  const intValue = typeof value === "number" ? value : parseInt(stringValue ?? "", 10);

  return enumValues.some(current => {
    if (normalizedString && current.name.toLowerCase() === normalizedString) {
      return true;
    }
    return current.value === intValue;
  });
}

export const allWeightTonnageTypes = [
  VehicleInfoTonnagePriceCategoryType.LessThanOrEqualTo3500Kilos,
  VehicleInfoTonnagePriceCategoryType.GreaterThan3500Kilos,
  VehicleInfoTonnagePriceCategoryType.GreaterThan7500Kilos
];

export function isWeightTonnageType(value: VehicleInfoTonnagePriceCategoryType) {
  return allWeightTonnageTypes.includes(getEnumValue(value, VehicleInfoTonnagePriceCategoryType));
}

export function containsAllWeightTonnageTypes(values: VehicleInfoTonnagePriceCategoryType[]) {
  const weightTonnageTypes: VehicleInfoTonnagePriceCategoryType[] = [];
  values.forEach(value => {
    const enumValue = getEnumValue(value, VehicleInfoTonnagePriceCategoryType);
    if (allWeightTonnageTypes.includes(enumValue)) {
      weightTonnageTypes.push(enumValue);
    }
  });
  return weightTonnageTypes.length === allWeightTonnageTypes.length;
}

export function isOnlyAllWeightTonnageTypes(values: VehicleInfoTonnagePriceCategoryType[]) {
  return values.length === allWeightTonnageTypes.length && containsAllWeightTonnageTypes(values);
}

export function containsWeightTonnageType(values: VehicleInfoTonnagePriceCategoryType[]) {
  return values.some(value => isWeightTonnageType(value));
}

export function containsSameTonnageType(
  lhs: VehicleInfoTonnagePriceCategoryType[],
  rhs: VehicleInfoTonnagePriceCategoryType[]
) {
  if (lhs.length === 0 || rhs.length === 0) {
    return false;
  }
  for (const lhsTonnageType of lhs) {
    for (const rhsTonnageType of rhs) {
      if (lhsTonnageType === rhsTonnageType) {
        return true;
      }
    }
  }
  return false;
}

export function isSameTonnageTypes(
  lhs: VehicleInfoTonnagePriceCategoryType[],
  rhs: VehicleInfoTonnagePriceCategoryType[]
) {
  if (lhs.length === 0 || rhs.length === 0) {
    return false;
  }
  let totalMatching = 0;
  for (const lhsTonnageType of lhs) {
    for (const rhsTonnageType of rhs) {
      if (lhsTonnageType === rhsTonnageType) {
        totalMatching++;
      }
    }
  }
  return totalMatching === lhs.length && totalMatching === rhs.length;
}
